/**
 * StatusBadge - Clara Vale auto-detecting status pill.
 *
 * Renders a coloured pill badge that maps status strings to the CV semantic palette.
 * Uses a sentence-case rounded pill and Clara Vale colour tokens.
 *
 * Usage:
 *   <StatusBadge status="active" />
 *   <StatusBadge status="Under Review" />
 *   <StatusBadge status="Decided" color="#1c5a48" />
 */

import type { ReactNode } from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { cn } from '@/shared/lib/utils'
type BadgeVariant = 'active' | 'draft' | 'info' | 'in-progress' | 'warning' | 'archived' | 'error' | 'neutral'

interface StatusBadgeProps {
  /** The status string - auto-detected to a variant */
  status: string
  /** Optional explicit colour from API (e.g. workflow state colour). Falls back to auto-detection. */
  color?: string | null
  className?: string
  icon?: ReactNode
  tooltip?: string
}

const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  active: 'bg-[var(--status-ok-bg)] text-[var(--status-ok)]',
  // Draft / pending / new → same soft-grey pill used by the inactive
  // filter tabs. Replaces the previous amber treatment; actual warnings
  // still go through the `warning` variant below which keeps amber.
  draft: 'bg-[var(--bg-subtle)] text-[var(--ink-2)]',
  info: 'bg-[var(--status-info-bg)] text-[var(--status-info)]',
  'in-progress': 'bg-[var(--status-in-progress-bg)] text-[var(--status-in-progress)]',
  warning: 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]',
  archived: 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
  error: 'bg-[var(--status-error-bg)] text-[var(--status-error)]',
  neutral: 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
}

/** Map a status string to a Clara Vale variant */
function detectVariant(status: string): BadgeVariant {
  const s = status.toLowerCase().replace(/[_\-.]/g, ' ')

  // Negative / incomplete terminal states. Must precede the success check
  // so "not completed" / "incomplete" don't match the "completed" keyword.
  if (/\b(not completed|incomplete)\b/.test(s)) return 'neutral'

  // Awaiting an action / needs attention (must precede the draft check so
  // "repair pending" isn't swallowed by the generic "pending" -> draft rule).
  if (/\brepair pending\b/.test(s)) return 'warning'

  // Active / success (incl. a reviewed lab sample - terminal, done, and a
  // passed / completed inspection).
  if (
    /\b(active|approved|completed|complete|decided|settled|paid|processed|reviewed|passed)\b/.test(
      s,
    )
  )
    return 'active'

  // Draft / pending
  if (/\b(draft|pending|new)\b/.test(s)) return 'draft'

  // In progress / under review (warm sand tone)
  if (/\b(under review|review|in progress|processing|on hold)\b/.test(s)) return 'in-progress'

  // Submitted / received / scheduled (muted olive) - e.g. a lab sample in
  // hand, or an inspection booked for a future date.
  if (/\b(submitted|received|scheduled)\b/.test(s)) return 'info'

  // Warnings / docs needed / awaiting an external item (e.g. a requested
  // lab sample that has not arrived yet)
  if (/\b(docs requested|requested|inspection|awaiting|expir)/i.test(s)) return 'warning'

  // Errors
  if (/\b(error|failed|rejected|denied)\b/.test(s)) return 'error'

  // Archived / closed
  if (/\b(archived|inactive|closed|cancelled|canceled)\b/.test(s)) return 'archived'

  // Unknown - default to neutral
  return 'neutral'
}

function humanizeStatus(status: string): string {
  const words = status.replace(/[_\-.]+/g, ' ').replace(/\s+/g, ' ').trim()
  // Sentence case for machine-cased values ("ACTIVE", "in_progress"); a label
  // the API already cased ("Material & Labor Review") or a short acronym
  // ("LOA") is shown as written.
  const machineCased =
    (words === words.toUpperCase() && words.replace(/\s/g, '').length > 3) ||
    words === words.toLowerCase()
  if (!machineCased) return words
  const lower = words.toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

export function StatusBadge({
  status,
  color,
  className = '',
  icon,
  tooltip,
}: StatusBadgeProps) {
  const variant = detectVariant(status)
  // If the API provides an explicit colour and it's not in our detection map,
  // use it with a light tinted background
  const useApiColor = color && variant === 'neutral'

  return (
    <Badge
      title={tooltip}
      aria-label={tooltip ? `${humanizeStatus(status)}. ${tooltip}` : undefined}
      className={cn(
        'h-auto rounded-full px-2.5 py-0.5 text-[13px] font-medium',
        className,
        !useApiColor && VARIANT_CLASSES[variant]
      )}
      style={useApiColor ? { backgroundColor: `${color}14`, color } : undefined}
    >
      {icon}
      {humanizeStatus(status)}
    </Badge>
  )
}
